import { useEffect, useRef, useState } from "react";
import { debounce } from "@/lib/utils";

// Constants
const PARTICLE_STYLES = [
  { hue: "primary", gravity: "attract", opacity: 0.9, size: 1.2 },
  { hue: "primary", gravity: "repel", opacity: 0.6, size: 0.8 },
  { hue: "accent", gravity: "attract", opacity: 0.9, size: 1.2 },
  { hue: "accent", gravity: "repel", opacity: 0.6, size: 0.8 }
] as const;

const GRAVITY_ATTRACT = 0;
const GRAVITY_REPEL = 1;
const HUE_PRIMARY = 0;
const HUE_ACCENT = 1;
const MAX_NODES = 120;
const MIN_DISTANCE = 50;

// Helper function to get random number
const rnd = (min: number, max: number) => Math.random() * (max - min) + min;

// Helper to get colors
const getColors = () => {
    // Check if we are in browser environment
    if (typeof window === 'undefined') return { primary: "0 0% 0%", accent: "0 0% 0%" };

    const styles = getComputedStyle(document.documentElement);
    const primary = styles.getPropertyValue("--primary").trim();
    const accent = styles.getPropertyValue("--accent").trim();
    return { primary, accent };
};

class ParticleSystem {
    // SoA Arrays
    nodesX = new Float32Array(MAX_NODES);
    nodesY = new Float32Array(MAX_NODES);
    nodesVX = new Float32Array(MAX_NODES);
    nodesVY = new Float32Array(MAX_NODES);
    nodesR = new Float32Array(MAX_NODES);
    nodesStyleIndex = new Uint8Array(MAX_NODES);
    nodesGravity = new Uint8Array(MAX_NODES);
    nodesIndices = new Uint8Array(MAX_NODES);

    nodeCount = 0;

    // Rendering & Style
    lineBins: number[][];
    binColors: string[];
    fillStyles: string[];

    cachedWidth = 0;
    cachedHeight = 0;
    linkDistance = 120;

    // Config
    reduceMotion = false;
    primary = "";
    accent = "";

    // Optimization
    lastPerfCheckTime = 0;
    BIN_COUNT = 6;
    BIN_ALPHAS = [0.06, 0.12, 0.18, 0.24, 0.30, 0.36];

    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;

    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        const ctx = canvas.getContext("2d", { alpha: true });
        if (!ctx) throw new Error("Could not get 2d context");
        this.ctx = ctx;

        this.lineBins = Array.from({ length: this.BIN_COUNT }, () => []);
        this.binColors = Array(this.BIN_COUNT).fill("");
        this.fillStyles = Array(PARTICLE_STYLES.length).fill("");

        if (typeof window !== 'undefined') {
             this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        }

        this.updateColors();
    }

    updateColors() {
        const { primary, accent } = getColors();
        this.primary = primary;
        this.accent = accent;

        for (let i = 0; i < this.BIN_COUNT; i++) {
            this.binColors[i] = `hsl(${this.accent} / ${this.BIN_ALPHAS[i]})`;
        }

        for (let i = 0; i < PARTICLE_STYLES.length; i++) {
            const style = PARTICLE_STYLES[i];
            const colorVar = style.hue === "primary" ? this.primary : this.accent;
            this.fillStyles[i] = `hsl(${colorVar} / ${style.opacity})`;
        }
    }

    dpr() {
        return Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
    }

    resize() {
        const ratio = this.dpr();
        this.cachedWidth = window.innerWidth;
        this.cachedHeight = window.innerHeight;
        const w = this.cachedWidth;
        const h = this.cachedHeight;

        this.canvas.width = Math.floor(w * ratio);
        this.canvas.height = Math.floor(h * ratio);
        this.canvas.style.width = `${w}px`;
        this.canvas.style.height = `${h}px`;
        this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

        const area = w * h;
        const density = this.reduceMotion ? 0.00002 : 0.00005;
        const targetCount = Math.max(20, Math.min(80, Math.floor(area * density)));
        this.linkDistance = Math.max(60, Math.min(100, Math.sqrt(area) * 0.08));

        this.adjustNodeCount(targetCount);
    }

    addNode(x: number, y: number, vx: number, vy: number, r: number, gravity: number, styleIndex: number) {
        if (this.nodeCount >= MAX_NODES) return;
        const i = this.nodeCount;
        this.nodesX[i] = x;
        this.nodesY[i] = y;
        this.nodesVX[i] = vx;
        this.nodesVY[i] = vy;
        this.nodesR[i] = r;
        this.nodesGravity[i] = gravity;
        this.nodesStyleIndex[i] = styleIndex;
        this.nodesIndices[i] = i;
        this.nodeCount++;
    }

    removeOldest() {
        if (this.nodeCount > 0) {
            let removeIdx = -1;
            for (let k = 0; k < this.nodeCount; k++) {
                if (this.nodesIndices[k] === 0) {
                    removeIdx = k;
                    break;
                }
            }

            if (removeIdx !== -1) {
                this.nodesIndices.copyWithin(removeIdx, removeIdx + 1, this.nodeCount);
            }

            for (let k = 0; k < this.nodeCount - 1; k++) {
                this.nodesIndices[k]--;
            }

            this.nodesX.copyWithin(0, 1, this.nodeCount);
            this.nodesY.copyWithin(0, 1, this.nodeCount);
            this.nodesVX.copyWithin(0, 1, this.nodeCount);
            this.nodesVY.copyWithin(0, 1, this.nodeCount);
            this.nodesR.copyWithin(0, 1, this.nodeCount);
            this.nodesStyleIndex.copyWithin(0, 1, this.nodeCount);
            this.nodesGravity.copyWithin(0, 1, this.nodeCount);
            this.nodeCount--;
        }
    }

    initNodes(count: number) {
        this.nodeCount = 0;
        for (let i = 0; i < count; i++) {
            this.spawnNode();
        }
    }

    spawnNode() {
        let attempts = 0;
        let x, y;
        const minDistanceSq = MIN_DISTANCE * MIN_DISTANCE;

        do {
            x = rnd(0, this.cachedWidth);
            y = rnd(0, this.cachedHeight);
            attempts++;
            if (attempts < 100) {
                let tooClose = false;
                for (let j = 0; j < this.nodeCount; j++) {
                    const dx = this.nodesX[j] - x;
                    const dy = this.nodesY[j] - y;
                    if (dx * dx + dy * dy < minDistanceSq) {
                        tooClose = true;
                        break;
                    }
                }
                if (!tooClose) break;
            }
        } while (attempts < 100);

        const hueType = Math.random() < 0.25 ? HUE_ACCENT : HUE_PRIMARY;
        const gravityType = Math.random() < 0.4 ? GRAVITY_ATTRACT : GRAVITY_REPEL;
        const styleIndex = (hueType === HUE_ACCENT ? 2 : 0) + (gravityType === GRAVITY_REPEL ? 1 : 0);

        this.addNode(
            x, y,
            rnd(-0.3, 0.3) * (this.reduceMotion ? 0.3 : 1),
            rnd(-0.3, 0.3) * (this.reduceMotion ? 0.3 : 1),
            rnd(1.2, 2.2),
            gravityType,
            styleIndex
        );
    }

    adjustNodeCount(target: number) {
        if (this.nodeCount === 0) {
            this.initNodes(target);
            return;
        }
        if (this.nodeCount < target) {
            const toAdd = target - this.nodeCount;
            for (let i = 0; i < toAdd; i++) {
                this.spawnNode();
            }
        } else if (this.nodeCount > target) {
            this.nodeCount = target;
        }
    }

    createParticlesAtPosition(x: number, y: number) {
        const particleCount = Math.floor(Math.random() * 6) + 5;
        const MAX_PARTICLES_INTERACTION = 110;

        for (let i = 0; i < particleCount; i++) {
             let attempts = 0;
             let newX = x;
             let newY = y;

             while (attempts < 50) {
                const angle = Math.random() * Math.PI * 2;
                const maxSpawnDistance = this.cachedWidth < 768 ? 80 : Math.min(400, MIN_DISTANCE * 1.5);
                const minSpawnDistance = this.cachedWidth < 768 ? 30 : Math.min(150, MIN_DISTANCE * 0.6);
                const distance = Math.random() * (maxSpawnDistance - minSpawnDistance) + minSpawnDistance;
                newX = x + Math.cos(angle) * distance;
                newY = y + Math.sin(angle) * distance;

                const minDistanceSq = MIN_DISTANCE * MIN_DISTANCE;
                let tooClose = false;
                for (let j = 0; j < this.nodeCount; j++) {
                     const dx = this.nodesX[j] - newX;
                     const dy = this.nodesY[j] - newY;
                     if (dx * dx + dy * dy < minDistanceSq) {
                         tooClose = true;
                         break;
                     }
                }
                if (!tooClose) break;
                attempts++;
             }

            newX = Math.max(15, Math.min(this.cachedWidth - 15, newX));
            newY = Math.max(15, Math.min(this.cachedHeight - 15, newY));

            const hueType = Math.random() < 0.25 ? HUE_ACCENT : HUE_PRIMARY;
            const gravityType = Math.random() < 0.4 ? GRAVITY_ATTRACT : GRAVITY_REPEL;
            const styleIndex = (hueType === HUE_ACCENT ? 2 : 0) + (gravityType === GRAVITY_REPEL ? 1 : 0);

            this.addNode(
                newX, newY,
                rnd(-1, 1), rnd(-1, 1),
                rnd(1.2, 2.2), gravityType, styleIndex
            );

            if (this.nodeCount > MAX_PARTICLES_INTERACTION) {
                this.removeOldest();
            }
        }
    }

    step(mouse: { x: number, y: number } | null, touches: { x: number, y: number }[]) {
        const w = this.cachedWidth;
        const h = this.cachedHeight;
        const currentTime = Date.now();
        const frameDelta = currentTime - this.lastPerfCheckTime;
        this.lastPerfCheckTime = currentTime;

        if (Math.random() < 0.05) {
             if (frameDelta > 60) {
                 if (this.nodeCount > 40) this.nodeCount = Math.max(40, this.nodeCount - 1);
             }
        }

        this.ctx.clearRect(0, 0, w, h);

        const maxDist = Math.max(200, this.linkDistance);
        const maxDistSq = maxDist * maxDist;
        const mouseRadius = 150;
        const mouseRadiusSq = mouseRadius * mouseRadius;
        const pushForce = 0.4;

        // Mouse Force
        if (mouse) {
            const { x: mx, y: my } = mouse;
            for (let i = 0; i < this.nodeCount; i++) {
                const dx = this.nodesX[i] - mx;
                const dy = this.nodesY[i] - my;
                const distSq = dx * dx + dy * dy;

                if (distSq < mouseRadiusSq && distSq > 0) {
                    const dist = Math.sqrt(distSq);
                    const force = (1 - dist / mouseRadius) * pushForce;
                    this.nodesVX[i] += (dx / dist) * force;
                    this.nodesVY[i] += (dy / dist) * force;
                }
            }
        }

        // Touch Forces
         if (touches.length > 0) {
            for (const touch of touches) {
                const { x: tx, y: ty } = touch;
                for (let i = 0; i < this.nodeCount; i++) {
                    const dx = this.nodesX[i] - tx;
                    const dy = this.nodesY[i] - ty;
                    const distSq = dx * dx + dy * dy;

                    if (distSq < mouseRadiusSq && distSq > 0) {
                    const dist = Math.sqrt(distSq);
                    const force = (1 - dist / mouseRadius) * pushForce;
                    this.nodesVX[i] += (dx / dist) * force;
                    this.nodesVY[i] += (dy / dist) * force;
                    }
                }
            }
        }

        // Reset bins
        for (let i = 0; i < this.BIN_COUNT; i++) {
            this.lineBins[i].length = 0;
        }

        // Spatial Sort
        for (let i = 1; i < this.nodeCount; i++) {
            const current = this.nodesIndices[i];
            const currentX = this.nodesX[current];
            let j = i - 1;
            while (j >= 0 && this.nodesX[this.nodesIndices[j]] > currentX) {
                this.nodesIndices[j + 1] = this.nodesIndices[j];
                j--;
            }
            this.nodesIndices[j + 1] = current;
        }

        // Interactions between nodes
         for (let k = 0; k < this.nodeCount; k++) {
            const i = this.nodesIndices[k];
            const nX = this.nodesX[i];
            const nY = this.nodesY[i];

            for (let m = k + 1; m < this.nodeCount; m++) {
                const j = this.nodesIndices[m];
                const otherX = this.nodesX[j];

                const dx = otherX - nX;
                if (dx > maxDist) break;

                const dy = this.nodesY[j] - nY;
                if (dy > maxDist || dy < -maxDist) continue;

                const otherY = this.nodesY[j];
                const distSq = dx * dx + dy * dy;
                if (distSq > maxDistSq) continue;

                const dist = Math.sqrt(distSq);

                if (dist < MIN_DISTANCE && dist > 0) {
                    const repulsionForce = (1 - dist / MIN_DISTANCE) * 0.8;
                    const fx = -(dx / dist) * repulsionForce;
                    const fy = -(dy / dist) * repulsionForce;
                    this.nodesVX[i] += fx;
                    this.nodesVY[i] += fy;
                    this.nodesVX[j] -= fx;
                    this.nodesVY[j] -= fy;
                }

                if (dist < 200 && dist > 15) {
                    const force = (200 - dist) / 200 * 0.02;
                    const fx = (dx / dist) * force;
                    const fy = (dy / dist) * force;

                    const g1 = this.nodesGravity[i];
                    const g2 = this.nodesGravity[j];

                    let scalar = 0.7; // default mixed
                    let same = false;

                    if (g1 === GRAVITY_ATTRACT && g2 === GRAVITY_ATTRACT) {
                        scalar = 0.4;
                        this.nodesVX[i] += fx * scalar;
                        this.nodesVY[i] += fy * scalar;
                        this.nodesVX[j] -= fx * scalar;
                        this.nodesVY[j] -= fy * scalar;
                        same = true;
                    } else if (g1 === GRAVITY_REPEL && g2 === GRAVITY_REPEL) {
                        scalar = 0.6;
                        this.nodesVX[i] -= fx * scalar;
                        this.nodesVY[i] -= fy * scalar;
                        this.nodesVX[j] += fx * scalar;
                        this.nodesVY[j] += fy * scalar;
                        same = true;
                    }

                    if (!same) {
                         if (g1 === GRAVITY_ATTRACT) {
                            this.nodesVX[i] += fx * scalar;
                            this.nodesVY[i] += fy * scalar;
                            this.nodesVX[j] += fx * scalar;
                            this.nodesVY[j] += fy * scalar;
                        } else {
                            this.nodesVX[i] -= fx * scalar;
                            this.nodesVY[i] -= fy * scalar;
                            this.nodesVX[j] -= fx * scalar;
                            this.nodesVY[j] -= fy * scalar;
                        }
                    }

                    if (dist < 25) {
                        const repelForce = (25 - dist) / 25 * 0.1;
                        const rfx = (dx / dist) * repelForce;
                        const rfy = (dy / dist) * repelForce;
                        this.nodesVX[i] -= rfx;
                        this.nodesVY[i] -= rfy;
                        this.nodesVX[j] += rfx;
                        this.nodesVY[j] += rfy;
                    }
                }

                if (dist < this.linkDistance) {
                    const alphaVal = Math.max(0.08, 1 - dist / this.linkDistance) * 0.35;
                    let binIdx = Math.floor(alphaVal * 15);
                    if (binIdx < 0) binIdx = 0;
                    if (binIdx >= this.BIN_COUNT) binIdx = this.BIN_COUNT - 1;
                    this.lineBins[binIdx].push(nX, nY, otherX, otherY);
                }
            }
        }

        // Draw Lines
        this.ctx.lineWidth = 1.5;
        this.ctx.shadowBlur = 0;

        for (let i = 0; i < this.BIN_COUNT; i++) {
            const bin = this.lineBins[i];
            if (bin.length === 0) continue;
            this.ctx.strokeStyle = this.binColors[i];
            this.ctx.beginPath();
            for (let k = 0; k < bin.length; k += 4) {
                this.ctx.moveTo(bin[k], bin[k + 1]);
                this.ctx.lineTo(bin[k + 2], bin[k + 3]);
            }
            this.ctx.stroke();
        }

        // Draw Nodes
        for (let i = 0; i < PARTICLE_STYLES.length; i++) {
            const style = PARTICLE_STYLES[i];
            this.ctx.fillStyle = this.fillStyles[i];
            this.ctx.beginPath();
            let hasElements = false;
            for (let j = 0; j < this.nodeCount; j++) {
                if (this.nodesStyleIndex[j] === i) {
                    const r = this.nodesR[j] * style.size;
                    this.ctx.moveTo(this.nodesX[j] + r, this.nodesY[j]);
                    this.ctx.arc(this.nodesX[j], this.nodesY[j], r, 0, Math.PI * 2);
                    hasElements = true;
                }
            }
            if (hasElements) this.ctx.fill();
        }

        // Update Positions
        const edgeForce = 50;
        for (let i = 0; i < this.nodeCount; i++) {
             if (this.nodesX[i] < edgeForce) {
                this.nodesVX[i] += (edgeForce - this.nodesX[i]) / edgeForce * 0.1;
             }
             if (this.nodesX[i] > w - edgeForce) {
                this.nodesVX[i] -= (this.nodesX[i] - (w - edgeForce)) / edgeForce * 0.1;
             }
             if (this.nodesY[i] < edgeForce) {
                this.nodesVY[i] += (edgeForce - this.nodesY[i]) / edgeForce * 0.1;
             }
             if (this.nodesY[i] > h - edgeForce) {
                this.nodesVY[i] -= (this.nodesY[i] - (h - edgeForce)) / edgeForce * 0.1;
             }

             this.nodesVX[i] *= 0.98;
             this.nodesVY[i] *= 0.98;

             const randomForce = 0.02;
             this.nodesVX[i] += rnd(-randomForce, randomForce);
             this.nodesVY[i] += rnd(-randomForce, randomForce);

             this.nodesX[i] += this.nodesVX[i];
             this.nodesY[i] += this.nodesVY[i];

             if (this.nodesX[i] <= 0 || this.nodesX[i] >= w) {
                 this.nodesVX[i] *= -0.8;
                 this.nodesX[i] = Math.max(0, Math.min(w, this.nodesX[i]));
             }
             if (this.nodesY[i] <= 0 || this.nodesY[i] >= h) {
                 this.nodesVY[i] *= -0.8;
                 this.nodesY[i] = Math.max(0, Math.min(h, this.nodesY[i]));
             }
        }
    }
}

interface UseBackgroundCanvasProps {
    frozen?: boolean;
}

export const useBackgroundCanvas = ({ frozen = false }: UseBackgroundCanvasProps) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const rafRef = useRef<number | null>(null);
    const particleSystemRef = useRef<ParticleSystem | null>(null);
    const [isReady, setIsReady] = useState(false);

    const mouseRef = useRef<{ x: number, y: number } | null>(null);
    const touchesRef = useRef<{ x: number, y: number }[]>([]);
    const noParticlesRef = useRef<HTMLCollectionOf<Element> | null>(null);
    const frozenRef = useRef(frozen);

    useEffect(() => {
        frozenRef.current = frozen;
    }, [frozen]);

    // Initial Setup
    useEffect(() => {
        if (typeof window !== "undefined" && (window.__PRERENDER__ || navigator.userAgent.includes("AgileCoachPrerender"))) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const system = new ParticleSystem(canvas);
        particleSystemRef.current = system;

        system.resize();
        noParticlesRef.current = document.getElementsByClassName("no-particles");

        const loop = () => {
            if (frozenRef.current) {
                 rafRef.current = null;
                 return;
            }
            system.step(mouseRef.current, touchesRef.current);
            rafRef.current = requestAnimationFrame(loop);
        };

        if (!frozenRef.current) {
             rafRef.current = requestAnimationFrame(loop);
        }

        setIsReady(true);

        // --- Event Listeners ---

        const handleResize = debounce(() => {
            system.resize();
        }, 150);

        const handleThemeChange = () => {
            system.updateColors();
        };

        const handleInteraction = (x: number, y: number) => {
             system.createParticlesAtPosition(x, y);
        };

        const handleClick = (e: MouseEvent) => {
            if (frozenRef.current) return;
            const target = e.target as HTMLElement;
            const blockedElements = noParticlesRef.current;
            if (blockedElements) {
                for (let i = 0; i < blockedElements.length; i++) {
                    if (blockedElements[i].contains(target)) return;
                }
            }
            handleInteraction(e.clientX, e.clientY);
        };

        const handleMouseMove = (e: MouseEvent) => {
            if (frozenRef.current) return;
            mouseRef.current = { x: e.clientX, y: e.clientY };
        };

        const handleMouseLeave = () => {
            mouseRef.current = null;
        };

        const handleTouchStart = (e: TouchEvent) => {
            if (frozenRef.current) return;
            for (const touch of e.touches) {
                handleInteraction(touch.clientX, touch.clientY);
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (frozenRef.current) return;
            const touches: { x: number, y: number }[] = [];
            for (const touch of e.touches) {
                touches.push({ x: touch.clientX, y: touch.clientY });
            }
            touchesRef.current = touches;
        };

        const handleTouchEnd = () => {
            touchesRef.current = [];
        };

        const handleVisibility = () => {
             if (document.hidden) {
                if (rafRef.current) cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
             } else {
                 if (!rafRef.current && !frozenRef.current) {
                      rafRef.current = requestAnimationFrame(loop);
                 }
             }
        };

        window.addEventListener("resize", handleResize);
        window.addEventListener("click", handleClick);
        window.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseleave", handleMouseLeave);
        window.addEventListener("touchstart", handleTouchStart);
        window.addEventListener("touchmove", handleTouchMove, { passive: true });
        window.addEventListener("touchend", handleTouchEnd);
        window.addEventListener("touchcancel", handleTouchEnd);
        document.addEventListener("visibilitychange", handleVisibility);

        const observer = new MutationObserver(handleThemeChange);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ["style", "class"] });

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("click", handleClick);
            window.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseleave", handleMouseLeave);
            window.removeEventListener("touchstart", handleTouchStart);
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("touchend", handleTouchEnd);
            window.removeEventListener("touchcancel", handleTouchEnd);
            document.removeEventListener("visibilitychange", handleVisibility);
            observer.disconnect();
        };
    }, []); // Run once on mount

    // Handle frozen state changes
    useEffect(() => {
         if (typeof window !== "undefined" && (window.__PRERENDER__ || navigator.userAgent.includes("AgileCoachPrerender"))) return;

         if (!frozen && rafRef.current === null && particleSystemRef.current) {
             // Restart loop
             const loop = () => {
                 if (frozenRef.current) {
                     rafRef.current = null;
                     return;
                 }
                 particleSystemRef.current?.step(mouseRef.current, touchesRef.current);
                 rafRef.current = requestAnimationFrame(loop);
             };
             rafRef.current = requestAnimationFrame(loop);
         }
    }, [frozen]);

    return { canvasRef, isReady };
};
